import React, { useState, useEffect } from 'react';
import { getMerchantProfile, updateMerchantProfile } from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { showLoadingToast, updateToast } from '../../utils/toast';

const Profile = () => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [updating, setUpdating] = useState(false);

  useEffect(() => { loadProfile(); }, []);

  const loadProfile = async () => {
    try {
      const res = await getMerchantProfile();
      setProfile(res.data);
      setForm(res.data);
    } catch (err) {
      console.error('Failed to load profile:', err);
      toast.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    const toastId = showLoadingToast('Updating profile...');
    setUpdating(true);
    
    try {
      // Send only the required fields according to API spec
      const updateData = {
        merchantName: form.merchantName,
        email: form.email,
        companyName: form.companyName,
        companyAddress: form.companyAddress
      };
      
      const res = await updateMerchantProfile(updateData);
      
      setProfile(res.data);
      setForm(res.data);
      setEditing(false);
      updateToast(toastId, 'Profile updated successfully', 'success');
    } catch (err) {
      console.error('Failed to update profile:', err);
      updateToast(toastId, 'Failed to update profile', 'error');
    } finally {
      setUpdating(false);
    }
  };

  const handleCancel = () => {
    setEditing(false);
    setForm(profile);
  };

  if (loading) return <div className="flex justify-center p-8">Loading profile...</div>;

  return (
    <div>
      <h1 className="text-2xl font-bold mb-6">Profile Management</h1>
      <div className="grid lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <CardTitle className="text-xl">Account Details</CardTitle>
            {!editing && (
              <Button variant="outline" onClick={() => setEditing(true)} className="w-full sm:w-auto">
                Edit Profile
              </Button>
            )}
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Merchant ID</Label>
              <Input 
                value={profile?.merchantId || ''} 
                disabled 
                className="bg-gray-50" 
              />
            </div>
            
            <div>
              <Label>Full Name</Label>
              <Input 
                value={form.merchantName || ''} 
                onChange={(e) => setForm({...form, merchantName: e.target.value})} 
                disabled={!editing}
                placeholder="Enter your full name"
              />
            </div>
            
            <div>
              <Label>Email Address</Label>
              <Input 
                value={form.email || ''} 
                onChange={(e) => setForm({...form, email: e.target.value})} 
                disabled={!editing}
                type="email"
                placeholder="Enter your email"
              />
            </div>
            
            <div>
              <Label>Phone Number</Label>
              <Input 
                value={profile?.phone || ''} 
                disabled 
                className="bg-gray-50"
              />
            </div>
            
            <div>
              <Label>Company Name</Label>
              <Input 
                value={form.companyName || ''} 
                onChange={(e) => setForm({...form, companyName: e.target.value})} 
                disabled={!editing}
                placeholder="Enter company name"
              />
            </div>
            
            <div>
              <Label>Company Address</Label>
              <Input 
                value={form.companyAddress || ''} 
                onChange={(e) => setForm({...form, companyAddress: e.target.value})} 
                disabled={!editing}
                placeholder="Enter company address"
              />
            </div>
            
            {editing && (
              <div className="flex gap-2 pt-4">
                <Button 
                  onClick={handleSave}
                  disabled={updating}
                  className="flex-1"
                >
                  {updating ? 'Updating...' : 'Save Changes'}
                </Button>
                <Button 
                  variant="outline" 
                  onClick={handleCancel}
                  disabled={updating}
                  className="flex-1"
                >
                  Cancel
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader>
            <CardTitle className="text-xl">Account Status</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex justify-between items-center p-3 bg-gray-50 rounded">
              <span className="font-medium">Account Status</span>
              <Badge className={
                profile?.status === 'ACTIVE' ? 'bg-green-100 text-green-800' : 
                profile?.status === 'INACTIVE' ? 'bg-red-100 text-red-800' : 
                'bg-amber-100 text-amber-800'
              }>
                {profile?.status || 'PENDING'}
              </Badge>
            </div>
            
            <div className="flex justify-between items-center p-3 bg-gray-50 rounded">
              <span className="font-medium">KYC Status</span>
              <Badge className={
                profile?.kycStatus === 'APPROVED' ? 'bg-green-100 text-green-800' : 
                profile?.kycStatus === 'REJECTED' ? 'bg-red-100 text-red-800' : 
                'bg-amber-100 text-amber-800'
              }>
                {profile?.kycStatus || 'PENDING'}
              </Badge>
            </div>
            
            {profile?.kycRemarks && (
              <div className="p-3 bg-blue-50 rounded">
                <span className="font-medium text-blue-800">KYC Remarks</span>
                <p className="text-sm text-blue-700 mt-1">{profile.kycRemarks}</p>
              </div>
            )}
            
            <div className="flex justify-between items-center p-3 bg-gray-50 rounded">
              <span className="font-medium">Wallet Balance</span>
              <span className="font-bold text-green-600">
                ₹{(profile?.walletBalance || 0).toLocaleString('en-IN')}
              </span>
            </div>
            
            <div className="flex justify-between items-center p-3 bg-gray-50 rounded">
              <span className="font-medium">Usable Balance</span>
              <span className="font-bold text-blue-600">
                ₹{(profile?.usableBalance || 0).toLocaleString('en-IN')}
              </span>
            </div>
            
            <div className="flex justify-between items-center p-3 bg-gray-50 rounded">
              <span className="font-medium">Lien Amount</span>
              <span className="font-bold text-orange-600">
                ₹{(profile?.lienAmount || 0).toLocaleString('en-IN')}
              </span>
            </div>
            
            <div className="flex justify-between items-center p-3 bg-gray-50 rounded">
              <span className="font-medium">Member Since</span>
              <span className="text-sm">
                {profile?.createdAt ? new Date(profile.createdAt).toLocaleDateString('en-IN', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                }) : 'N/A'}
              </span>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Profile;
